/** Supported deployment environments, required to be here to avoid circular dependencies */
export type Environment = 'dev' | 'test' | 'staging' | 'prod';

// A singleton for the current environment
let _currentEnv: Environment;

// Exported read-only references
export const { get: currentEnv } = {
  get: () => {
    if (!_currentEnv) {
      throw new Error(
        'Environment must be set before it can be used. Call `initializeChainConfigEnv` first.'
      );
    }
    return _currentEnv;
  },
};

export function initializeChainConfigEnv(env: Environment) {
  // Prevent changing the environment type once it's been set
  if (_currentEnv && _currentEnv !== env) {
    throw new Error('Chain env cannot be changed once set');
  }
  // Set the environment
  _currentEnv = env;
}

export function _FORCE_SET_ENV_FOR_TEST(env: Environment | undefined) {
  // @ts-ignore error TS2580: Cannot find name 'process' DEPEND ON IMPORT PATH = using ts-ignore
  // eslint-disable-next-line turbo/no-undeclared-env-vars
  if (process.env.VITEST !== 'true') {
    throw new Error('This function can only be called within test suites');
  }
  // @ts-expect-error - operation not type safe, required for testing
  _currentEnv = undefined;
  if (env) {
    initializeChainConfigEnv(env);
  }
}

export function envOverrides<ValueType extends Record<string, any>>(
  base: ValueType,
  overrides: Partial<Record<Environment, Partial<ValueType>>> = {}
): ValueType {
  return new Proxy(base, {
    get(target, prop) {
      // Use base value if no environment is set
      const value = _currentEnv
        ? { ...target, ...(overrides[_currentEnv] || {}) }
        : target;

      if (prop === 'toJSON') return () => value;
      if (prop === 'toString') return () => JSON.stringify(value);
      return Reflect.get(value, prop);
    },
  });
}
